import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useLoader } from '@/composables/loader';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CPreview from '@/components/shared/CPreview.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import {
    getTeamMemberTemplates,
    getTeamMemberLibraries,
    removeTeamMemberEntities,
    addTeamMemberEntities,
    getTeamMemberStores,
    getTeamMemberOptionSets,
    getTeamMemberProducts,
    updateTeamMemberAccessRestriction,
    getTeamMemberAccessRestriction
} from '@/api/team';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { getTemplates } from '@/api/templateProduct';
import { EntityData, EntityType, TeamMember, AccessRestriction } from '@/api/typings/team';
import { getLibraries } from '@/api/libraries';
import { getStores } from '@/api/user';
import { getOptionSetsWithCount } from '@/api/storesApp/optionSets';
import { Store } from '@/typings/user';
import { getProductsWithCount } from '@/api/storesApp/products';
import { BDropdown } from 'bootstrap-vue/src/components/dropdown';

type EntityTypeNames = Record<EntityType, { singular: string; plural: string }>;


export default /*#__PURE__*/_defineComponent({
  __name: 'MemberAccessModal',
  props: {
    member: null,
    entityType: null
  },
  emits: ["permissions-saved", "member-updated", "cancel"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'permissions-saved', value: TeamMember): void;
    (event: 'member-updated', value: TeamMember): void;
    (event: 'cancel'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    member: TeamMember | undefined;
    entityType: EntityType;
};





const { execLoadedFn } = useLoader();

const entitiesAdded = ref<EntityData[]>([]);
const searchEntities = ref<EntityData[]>([]);
const entitiesToDisplay = ref<EntityData[]>([]);
const loading = ref(true);
const showStoresDropdown = ref(false);
const storesDropdown: Ref<BDropdown | null> = ref(null);
const availableStores = ref<Store[]>([]);
const selectedStore = ref<Store>();
const searchString = ref('');
const currentPage = ref(1);
const itemsPerPage = 10;
const totalCount = ref(0);
const mode = ref('list');
const checkedEntities = ref<EntityData[]>([]);
const generalCheck = computed(() => entitiesToDisplay.value.some((entity) => entity.isChecked));
const isSomethingChecked = computed(() => checkedEntities.value.some((entity) => entity.isChecked));

const accessRestriction = ref<AccessRestriction>(AccessRestriction.LISTED);

const entityTypeNames = ref<EntityTypeNames>({
    [EntityType.TEMPLATE]: { singular: 'template', plural: 'templates' },
    [EntityType.LIBRARY]: { singular: 'library', plural: 'libraries' },
    [EntityType.DESIGN]: { singular: 'design', plural: 'designs' },
    [EntityType.OPTION_SET]: { singular: 'option set', plural: 'option sets' },
    [EntityType.PRODUCT]: { singular: 'product', plural: 'products' },
    [EntityType.STORE]: { singular: 'store', plural: 'stores' },
    [EntityType.INTEGRATIONS]: { singular: 'integrations', plural: 'integrations' }
});

const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const tableFieldsWithThumbnail = [
    { key: 'isChecked', label: '', thStyle: { width: '50px', padding: '0.5rem' }, tdClass: 'checkCell' },
    { key: 'image', label: '' },
    { key: 'name', label: '' },
    { key: 'actions', label: '' }
];

const tableFields = [
    { key: 'isChecked', label: '', thStyle: { width: '50px', padding: '0.5rem' }, tdClass: 'checkCell' },
    { key: 'name', label: '' },
    { key: 'actions', label: '' }
];

const getTableFields = () => {
    if (props.entityType == EntityType.STORE) {
        return tableFields;
    }

    if (props.entityType == EntityType.OPTION_SET) {
        return tableFields;
    }

    return tableFieldsWithThumbnail;
};

const cancelHandler = () => {
    if (mode.value == 'add') {
        changeToListMode();
    } else if (mode.value == 'list') {
        emit('cancel');
    }
};

const forceCloseHandler = () => {
    emit('cancel');
};

const confirmHandler = async () => {
    if (mode.value == 'add') {
        changeToListMode();
    } else if (mode.value == 'list') {
        emit('permissions-saved', props.member!);
    }
};

const removeEntityAccess = (entity: EntityData) => {
    const index = entitiesAdded.value.map((t) => t.id).indexOf(entity.id);
    entitiesAdded.value.splice(index, 1);
    removeTeamMemberEntities(props.member!.user.Id, [entity.id], props.entityType);
};

const addEntityAccess = (entity: EntityData) => {
    entitiesAdded.value.push(entity);
    addTeamMemberEntities(props.member!.user.Id, [entity.id], props.entityType);
};

const changeCheckedEntityAccess = (type: string) => {
    checkedEntities.value = checkedEntities.value.map((entity) => {
        if (entity.isChecked) {
            if (type === 'add') addEntityAccess(entity);
            if (type === 'remove') removeEntityAccess(entity);
        }
        return { ...entity, isChecked: false };
    });
    checkAllPage(false);
};

const checkAllPage = (value: boolean) => {
    entitiesToDisplay.value = entitiesToDisplay.value.map((entity) => ({ ...entity, isChecked: value }));
    for (const entityToStore of entitiesToDisplay.value) {
        storeCheck(value, entityToStore);
    }
};

const storeCheck = (value: boolean, entity: EntityData) => {
    const isStored = checkedEntities.value.some((entityStored) => entityStored.id === entity.id);
    if (isStored) {
        checkedEntities.value = checkedEntities.value.map((entityStored) => {
            if (entityStored.id !== entity.id) return entityStored;
            return {
                ...entityStored,
                isChecked: value
            };
        });
    } else {
        checkedEntities.value.push({ ...entity, isChecked: value });
    }
};

const init = async () => {
    if (props.entityType == EntityType.PRODUCT) {
        showStoresDropdown.value = true;
        const storesResult = await getTeamMemberStores(props.member!.user.Id, 0, 100);
        availableStores.value = storesResult.Stores;
        if (availableStores.value && availableStores.value.length > 0) {
            selectedStore.value = availableStores.value[0];
        }
    }

    changeToListMode();

    if (props.entityType === EntityType.TEMPLATE && props.member?.user?.Id) {
        try {
            const initAccessRestriction = await getTeamMemberAccessRestriction(props.member.user.Id, props.entityType);
            accessRestriction.value = initAccessRestriction;
        } catch (error) {
            console.error(`Failed to load ${props.entityType} access restriction:`, error);
            accessRestriction.value = AccessRestriction.LISTED;
        }
    }
};

const changeToAddMode = () => {
    mode.value = 'add';
    searchString.value = '';
    currentPage.value = 1;

    fetchEntities();
};

const changeToListMode = () => {
    mode.value = 'list';
    searchString.value = '';
    currentPage.value = 1;

    fetchEntitiesWithAccess();
};

const fetchEntitiesWithAccess = async () => {
    switch (props.entityType) {
        case EntityType.TEMPLATE:
            return await fetchTemplatesWithAccess();
        case EntityType.LIBRARY:
            return await fetchLibrariesWithAccess();
        case EntityType.STORE:
            return await fetchStoresWithAccess();
        case EntityType.PRODUCT:
            return await fetchProductsWithAccess();
        case EntityType.OPTION_SET:
            return await fetchOptionSetsWithAccess();
    }
};

const fetchEntities = async () => {
    switch (props.entityType) {
        case EntityType.TEMPLATE:
            return await fetchTemplateProducts();
        case EntityType.LIBRARY:
            return await fetchLibraries();
        case EntityType.STORE:
            return await fetchStores();
        case EntityType.PRODUCT:
            return await fetchProducts();
        case EntityType.OPTION_SET:
            return await fetchOptionSets();
    }
};

const fetchTemplatesWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const templatesResult = await getTeamMemberTemplates(
            props.member!.user.Id,
            from,
            itemsPerPage,
            searchString.value
        );
        const mappedEntities = templatesResult.TemplateProducts.map((t) => ({
            id: t.id,
            name: t.name,
            thumbnail: buildCloudFrontUrl(t.thumbnailPath)
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = templatesResult.Count;
    } catch (error) {
        console.error('Failed to fetch templates with access', error);
    }
    loading.value = false;
};

const fetchLibrariesWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const librariesResult = await getTeamMemberLibraries(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = librariesResult.Libraries.map((l) => ({
            id: l.LibraryId.toString(),
            name: l.Name,
            thumbnail: l.ThumbnailPath ? buildCloudFrontUrl(l.ThumbnailPath) : ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = librariesResult.Count;
    } catch (error) {
        console.error('Failed to fetch libraries with access', error);
    }
    loading.value = false;
};

const fetchStoresWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const storesResult = await getTeamMemberStores(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = storesResult.Stores.map((s) => ({
            id: s.Id.toString(),
            name: s.Url,
            thumbnail: ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = storesResult.Count;
    } catch (error) {
        console.error('Failed to fetch stores with access', error);
    }
    loading.value = false;
};

const fetchProductsWithAccess = async () => {
    if (mode.value == 'add' || !selectedStore.value?.Url) {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const productsResult = await getTeamMemberProducts(
            props.member!.user.Id,
            from,
            to,
            selectedStore.value?.Url,
            searchString.value
        );
        const mappedEntities = productsResult.Products.map((p) => ({
            id: `${p.productId}`,
            name: p.name,
            thumbnail: p.imageSrc
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = productsResult.Count;
    } catch (error) {
        console.error('Failed to fetch products with access', error);
    }
    loading.value = false;
};

const fetchOptionSetsWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const setsResult = await getTeamMemberOptionSets(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = setsResult.OptionSets.map((s) => ({
            id: s.id,
            name: s.title,
            thumbnail: ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = setsResult.Count;
    } catch (error) {
        console.error('Failed to fetch option sets with access', error);
    }
    loading.value = false;
};

const fetchTemplateProducts = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        let templatesResult = await getTemplates(from, itemsPerPage, searchString.value);
        const mappedEntities = templatesResult.templateProducts.map((t) => ({
            id: t.id,
            name: t.name,
            thumbnail: buildCloudFrontUrl(t.thumbnailPath),
            isChecked: checkedEntities.value?.some((entity) => entity.id === t.id && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = templatesResult.totalCount;
    } catch (error) {
        console.error('Failed to fetch templates', error);
    }
    loading.value = false;
};

const fetchLibraries = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const librariesResult = await getLibraries([0, 1, 2, 3], currentPage.value, itemsPerPage, searchString.value);
        const mappedEntities = librariesResult.Libraries.map((l) => ({
            id: l.LibraryId.toString(),
            name: l.Name,
            thumbnail: l.ThumbnailPath ? buildCloudFrontUrl(l.ThumbnailPath) : '',
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === l.LibraryId.toString() && entity.isChecked) ||
                false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = librariesResult.Count;
    } catch (error) {
        console.error('Failed to fetch libraries', error);
    }
    loading.value = false;
};

const fetchStores = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const stores = await getStores(from, to, searchString.value);
        const mappedEntities = stores.Stores.map((l) => ({
            id: l.Id.toString(),
            name: l.Url,
            thumbnail: '',
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === l.Id.toString() && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = stores.Count;
    } catch (error) {
        console.error('Failed to fetch stores', error);
    }
    loading.value = false;
};

const fetchProducts = async () => {
    if (mode.value == 'list' || !selectedStore.value?.Url) {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const products = await getProductsWithCount(
            selectedStore.value?.Url,
            searchString.value,
            from,
            itemsPerPage,
            true,
            0
        );
        const mappedEntities = products.products.map((p) => ({
            id: `${p.productId}`,
            name: p.name,
            thumbnail: p.imageSrc,
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === `${p.productId}` && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = products.total;
    } catch (error) {
        console.error('Failed to fetch products', error);
    }
    loading.value = false;
};

const fetchOptionSets = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const optionSets = await getOptionSetsWithCount(searchString.value, from, itemsPerPage);
        const mappedEntities = optionSets.sets.map((o) => ({
            id: o.id,
            name: o.title,
            thumbnail: '',
            isChecked: checkedEntities.value?.some((entity) => entity.id === o.id && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = optionSets.totalCount;
    } catch (error) {
        console.error('Failed to fetch option sets', error);
    }
    loading.value = false;
};

const updateAccessRestriction = async (accessRestrictionParam: AccessRestriction, entityType: EntityType) => {
    if (!props.member?.user?.Id) {
        console.error('updateAccessRestriction: Member user ID is not available');
        return;
    }

    const previousMode = accessRestriction.value;

    try {
        loading.value = true;
        accessRestriction.value = accessRestrictionParam;
        await updateTeamMemberAccessRestriction(props.member.user.Id, accessRestrictionParam, entityType);

        // Update the member object and emit to parent for persistence
        if (props.member) {
            props.member.accessRestriction = accessRestrictionParam;
            // Create a copy of the updated member to emit
            const updatedMember = { ...props.member };
            emit('member-updated', updatedMember);
        }
    } catch (error) {
        // Revert on error
        accessRestriction.value = previousMode;
        console.error(
            `Failed to update for entityType ${entityType} accesssRestriction to ${accessRestrictionParam} for member: ${props.member.user.Id}`,
            error
        );

        const errorMessage = error instanceof Error ? error.message : error?.toString();
        alert(
            `Failed to update for entityType ${entityType} accessRestriction to ${accessRestrictionParam}, error: ${errorMessage}`
        );
    } finally {
        loading.value = false;
    }
};

const onStoresDropdownClick = (store: Store) => {
    if (selectedStore.value !== store) {
        selectedStore.value = store;
        if (mode.value == 'list') {
            fetchEntitiesWithAccess();
        } else {
            fetchEntities();
        }
    }
    storesDropdown.value?.show();
};

const getAccessModeText = (mode: AccessRestriction) => {
    switch (mode) {
        case AccessRestriction.LISTED:
            return 'Listed';
        case AccessRestriction.FULL_ACCESS:
            return 'Full Access';
        case AccessRestriction.ALL_READ_ONLY_PLUS_LISTED:
            return 'All Read-Only + Full Access to Listed';
        default:
            return 'Listed';
    }
};

onMounted(init);

watch([searchString, currentPage], () => {
    fetchEntitiesWithAccess();
    fetchEntities();
});

return { __sfc: true,emit, props, execLoadedFn, entitiesAdded, searchEntities, entitiesToDisplay, loading, showStoresDropdown, storesDropdown, availableStores, selectedStore, searchString, currentPage, itemsPerPage, totalCount, mode, checkedEntities, generalCheck, isSomethingChecked, accessRestriction, entityTypeNames, capitalizeFirstLetter, tableFieldsWithThumbnail, tableFields, getTableFields, cancelHandler, forceCloseHandler, confirmHandler, removeEntityAccess, addEntityAccess, changeCheckedEntityAccess, checkAllPage, storeCheck, init, changeToAddMode, changeToListMode, fetchEntitiesWithAccess, fetchEntities, fetchTemplatesWithAccess, fetchLibrariesWithAccess, fetchStoresWithAccess, fetchProductsWithAccess, fetchOptionSetsWithAccess, fetchTemplateProducts, fetchLibraries, fetchStores, fetchProducts, fetchOptionSets, updateAccessRestriction, onStoresDropdownClick, getAccessModeText, CModal, CButton, CPreview, CCheckbox, EntityType, AccessRestriction, BDropdown }
}

})